import axios from "axios";
import React, { useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Button, Input, P } from "@upsolve/ui";

import trackComponentAction from "../analytics/trackComponentAction";
import trackComponentImpression from "../analytics/trackComponentImpression";
import getMyUpsolveEntryUrl from "../Links/getMyUpsolveEntryUrl";
import { MyUpsolveButtonLink } from "../Links/MyUpsolveLink";
import withGeoLocation from "../maps/withGeoLocation";

const promptTrackingProps = {
  componentName: "DisabledReadingPrompt",
  componentVersion: 1,
};

/**
 * DisabledReadingPrompt
 * - v0: Full width pop-up with simple question and keep reading button
 * - v1: Added link to bankruptcy myths
 *
 * @component
 * @version 0
 */
const DisabledReadingPrompt = (props) => {
  const [visible, setVisible] = useState(false);
  const [newYorkerEmail, setNewYorkerEmail] = useState("");
  const [newYorkerSubmitted, setNewYorkerSubmitted] = useState(false);
  const submitLog = () => {
    setNewYorkerSubmitted(true);
    axios.post(`${UPSOLVE_API_URL}/v1/logs/slack`, { text: newYorkerEmail, username: "New Yorkers" });
  };

  // On Mount
  useEffect(() => {
    const timeLastShown = localStorage.getItem("Upsolve.readingPrompt")
      ? Number(localStorage.getItem("Upsolve.readingPrompt"))
      : null;
    const canShow = !timeLastShown || new Date().getTime() - 1000 * 60 * 10 > timeLastShown;
    if (props.isAllowedToRender() && canShow) {
      trackComponentImpression({
        ...promptTrackingProps,
        pageContext: props.trackingPageContext,
      });
      localStorage.setItem("Upsolve.readingPrompt", new Date().getTime());
      setVisible(true);
    }
  }, [props.isAllowedToRender()]);

  return (
    visible && (
      <StyledDisabledReadingPrompt>
        {/* Delete this NY code after case launches */}
        {props.geoLocation?.region_code === "NY" ? (
          <div className="disabled-reading-prompt__new-yorker">
            <P>
              Have you ever had a debt collection lawsuit? Upsolve is working on a new program for users in New York.
              Please enter your email for more info:
            </P>
            <form
              className="buttons"
              onSubmit={(e) => {
                e.preventDefault();
                submitLog(newYorkerEmail);
              }}
            >
              <Input
                type="email"
                placeholder="your-name@email.com"
                disabled={newYorkerSubmitted}
                onChange={(e) => setNewYorkerEmail(e.target.value)}
                style={{ textAlign: "center" }}
              />
              <Button
                size="lg"
                disabled={
                  newYorkerSubmitted ||
                  newYorkerEmail.length < 4 ||
                  !newYorkerEmail.includes("@") ||
                  !newYorkerEmail.includes(".")
                }
              >
                Get More Info
              </Button>
              {newYorkerSubmitted && <P>Thank you for your interest! We'll reach out soon.</P>}
              <Button size="md" width="100%" inverted onClick={() => setVisible(false)}>
                Close
              </Button>
            </form>
          </div>
        ) : (
          <div>
            <P>Considering bankruptcy? We have a free tool to help you!</P>
            <div className="buttons">
              <MyUpsolveButtonLink
                size="lg"
                href={getMyUpsolveEntryUrl({
                  triggeredByActionId: "disabledReadingPrompt.myUpsolveNavigation",
                })}
                onClick={() => {
                  trackComponentAction({
                    ...promptTrackingProps,
                    actionId: "disabledReadingPrompt.myUpsolveNavigation",
                    actionMethod: "click",
                    pageContext: props.trackingPageContext,
                  });
                }}
              >
                Go to Free Bankruptcy Tool
              </MyUpsolveButtonLink>
              <Button
                size="lg"
                onClick={() => {
                  setVisible(false);
                  if (props.onClose) props.onClose();
                }}
              >
                Keep Reading
              </Button>
            </div>
            <GlobalStyledDisabledReadingPrompt />
          </div>
        )}
      </StyledDisabledReadingPrompt>
    )
  );
};

const StyledDisabledReadingPrompt = styled.div`
  position: fixed;
  z-index: 100;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  padding: 0.5em 1em 6em;
  border-top: 2px solid ${(props) => props.theme.colors.white[100]};
  background: linear-gradient(0deg, ${(props) => props.theme.colors.white[500]}, white);
  box-shadow: 0 0 140px -5px rgba(0, 0, 0, 0.5);
  text-align: center;
  & > p {
    font-weight: 500;
    font-size: 18px;
    max-width: 640px;
    text-align: center;
    margin: 0 auto;
  }
  .buttons {
    width: 400px;
    max-width: 100%;
    margin: 0 auto;
    padding: 0;
    & > a,
    & > button {
      width: 100%;
      margin-bottom: 0.5em;
      font-size: 18px;
      font-weight: 700;
    }
    & > p {
      text-align: center;
      text-decoration: underline;
      font-size: 18px;
      &:hover {
        cursor: pointer;
      }
    }
  }
  .disabled-reading-prompt__new-yorker {
    max-width: 640px;
    margin: 0 auto;
    p {
      font-weight: 900;
    }
    & > .buttons {
      text-align: center;
    }
    input {
      margin-bottom: 8px;
    }
  }
`;

const GlobalStyledDisabledReadingPrompt = createGlobalStyle`
  .content-body__container, .article__sidebar {
    filter: blur(4px);
    opacity: 0.7;
    pointer-events: none;
  }
`;

export default withGeoLocation(DisabledReadingPrompt);
